import { File, Plus, Download, Info } from "lucide-react";
import type { ModelWithFilesAndLinks } from "@maker-toolchest/shared";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { DeleteButton } from "@/components/DeleteButton";
import { FileMetadataDisplay } from "../FileMetadataDisplay";
import { formatFileSize } from "@/utils/formatting";

interface ModelFilesTabProps {
  model: ModelWithFilesAndLinks;
  onNavigate?: (path: string) => void;
  onAddFile: () => void;
  onDownloadFile: (fileId: string, fileName: string) => void;
  onDeleteFile: (fileId: string) => void;
  isDeleting: boolean;
}

export function ModelFilesTab({
  model,
  onNavigate,
  onAddFile,
  onDownloadFile,
  onDeleteFile,
  isDeleting,
}: ModelFilesTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Model Files</h3>
        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={onAddFile}>
          Add Files
        </Button>
      </div>

      {/* Files List */}
      {model.files.length === 0 ? (
        <EmptyState
          icon={<File className="h-10 w-10" />}
          title="No files yet"
          description="Upload model files to get started"
        />
      ) : (
        <div className="space-y-3">
          {model.files.map((file) => (
            <div
              key={file.id}
              className="p-4 border border-border rounded-lg bg-background hover:bg-background-subtle transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <File className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="font-medium text-foreground truncate">
                      {file.fileName}
                    </span>
                  </div>
                  <div className="mt-2 space-y-2">
                    <div className="text-xs text-foreground-muted">
                      <span className="font-medium">Size:</span>{" "}
                      {formatFileSize(file.fileSizeBytes)}
                    </div>
                    <FileMetadataDisplay modelId={model.id} file={file} />
                  </div>
                  {file.notes && (
                    <div className="mt-2 text-sm text-foreground-muted">
                      {file.notes}
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  {onNavigate && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Info className="h-4 w-4" />}
                      onClick={() =>
                        onNavigate(`/models/${model.id}/files/${file.id}/details`)
                      }
                    >
                      Details
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={() => onDownloadFile(file.id, file.fileName)}
                  >
                    Download
                  </Button>
                  <DeleteButton
                    variant="ghost"
                    onClick={() => onDeleteFile(file.id)}
                    disabled={isDeleting}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
